/**
 * Автодополнение в поле ввода: @участник, @роль, #канал, :эмодзи.
 *
 * В поле подставляется читаемый текст (`text`) — «@Ник», «#домашка», — а на сервер уходит
 * токен (`insert`): <@id>, <@&id>, <#id>, <:name:id>. Раньше токен вставлялся прямо в поле,
 * и человек видел в строке ввода голые идентификаторы.
 */
export interface Suggestion {
  key: string
  kind: 'user' | 'role' | 'channel' | 'emoji'
  label: string
  hint?: string | undefined
  color?: string | undefined
  /** Эмодзи: символ Unicode либо адрес картинки кастомного. */
  emoji?: string | undefined
  emojiUrl?: string | undefined
  insert: string
  /** Что видно в поле ввода; по умолчанию — то же, что уходит на сервер. */
  text?: string | undefined
  /** Участник — бот: в подсказке плашка «БОТ», как в списке участников и у сообщений. */
  bot?: boolean | undefined
  /** Адрес аватара участника; без него — кружок с буквой. */
  avatar?: string | undefined
}

/**
 * Возвращает текст сообщения с токенами вместо читаемых упоминаний.
 * Длинные подстановки заменяем первыми: «@Ник Петров» не должен пострадать от «@Ник».
 */
export function toTokens(text: string, picked: Map<string, string>): string {
  let out = text
  for (const [readable, token] of [...picked].sort((a, b) => b[0].length - a[0].length)) {
    if (readable === token) continue
    out = out.split(readable).join(token)
  }
  return out
}

const MENTION_TOKEN = /<@!?(\d+)>|<@&(\d+)>|<#(\d+)>/g

/**
 * Текст сообщения для правки: упоминания-токены (<@id>, <@&id>, <#id>) — в читаемый вид, как в поле
 * ввода, и карта для toTokens. Без этого правка показывала <@1552799385664884736> (правка №14).
 *
 * Токеном остаётся то, что назвать нечем, что внутри кода (там это просто текст), и то, чьё
 * читаемое имя уже есть в тексте само по себе или у другого упоминания: иначе при сохранении
 * toTokens превратил бы простой текст в упоминание.
 */
export function toReadable(
  text: string,
  name: (kind: 'user' | 'role' | 'channel', id: string) => string | undefined,
): { text: string; picked: Map<string, string> } {
  const picked = new Map<string, string>()
  // нечётные куски — код: ```блок``` и `строка`
  const parts = text.split(/(```[\s\S]*?```|`[^`\n]*`)/g)
  const plain = parts.filter((_, i) => i % 2 === 0).join('\n').replace(MENTION_TOKEN, ' ')
  const out = parts.map((part, i) =>
    i % 2 === 1
      ? part
      : part.replace(MENTION_TOKEN, (token, user?: string, role?: string, channel?: string) => {
          const kind = user ? 'user' : role ? 'role' : 'channel'
          const label = name(kind, (user ?? role ?? channel)!)
          if (!label) return token
          const shown = readable(kind, label)
          const taken = picked.get(shown)
          if ((taken && taken !== token) || plain.includes(shown)) return token
          picked.set(shown, token)
          return shown
        }),
  )
  return { text: out.join(''), picked }
}

export interface Trigger {
  kind: '@' | '#' | ':'
  query: string
  start: number // позиция символа-триггера в тексте
}

/** Ищет незакрытый @…, #… или :… (от двух символов) перед курсором. */
export function findTrigger(text: string, caret: number): Trigger | null {
  const before = text.slice(0, caret)
  const m = /(^|\s)([@#])([^\s@#]{0,32})$/.exec(before)
  if (m) {
    const kind = m[2] as '@' | '#'
    const query = m[3] ?? ''
    return { kind, query, start: caret - query.length - 1 }
  }
  const em = /(^|\s):([a-zA-Z0-9_+\-а-яё]{2,32})$/i.exec(before)
  if (em) {
    const query = em[2] ?? ''
    return { kind: ':', query, start: caret - query.length - 1 }
  }
  return null
}

/** Подставляет токен вместо @запрос и возвращает новый текст и позицию курсора. */
export function applySuggestion(text: string, trigger: Trigger, caret: number, insert: string): { text: string; caret: number } {
  const next = `${text.slice(0, trigger.start)}${insert} ${text.slice(caret)}`
  return { text: next, caret: trigger.start + insert.length + 1 }
}

/** Читаемый вид упоминания для поля ввода. */
export function readable(kind: Suggestion['kind'], label: string): string {
  switch (kind) {
    case 'user':
    case 'role':
      return label.startsWith('@') ? label : `@${label}`
    case 'channel':
      return `#${label}`
    default:
      return label
  }
}

export function matches(query: string, ...names: (string | null | undefined)[]): boolean {
  const q = query.toLowerCase()
  return !q || names.some((n) => n?.toLowerCase().includes(q))
}
